import {
  APPLICATION_DOMAINS,
  DOMAIN_LABELS,
  MODE_LABELS,
} from "../domain/config";
import { getDisplayChoices } from "../domain/choice-order";
import { AppError, ErrorClassification } from "../domain/errors";
import {
  AnswerOutcome,
  ExamMode,
  MasteryState,
  ViewState,
  type DomainProgress,
  type ProgressSummary,
  type Question,
  type ResponseMap,
  type ScoreReport,
} from "../domain/types";
import { PRACTICAL_LABS } from "../content/labs";
import { STUDY_RESOURCES } from "../content/resources";
import { STUDY_SECTIONS } from "../content/study";
import beginnerBookUrl from "../../CLOUDFLARE-BEGINNER-BOOK.md?url";

export const SubmissionState = {
  READY: "READY",
  UNANSWERED_WARNING: "UNANSWERED_WARNING",
} as const;

export type SubmissionState =
  (typeof SubmissionState)[keyof typeof SubmissionState];

export interface ExamRenderModel {
  readonly mode: ExamMode;
  readonly questions: readonly Question[];
  readonly cursorIndex: number;
  readonly responses: ResponseMap;
  readonly submissionState: SubmissionState;
}

export interface ResultsRenderModel {
  readonly mode: ExamMode;
  readonly questions: readonly Question[];
  readonly report: ScoreReport;
}

export type RenderModel =
  | {
      readonly view: typeof ViewState.DASHBOARD;
      readonly summary: ProgressSummary;
    }
  | {
      readonly view: typeof ViewState.STUDY;
      readonly summary: ProgressSummary;
    }
  | {
      readonly view: typeof ViewState.LABS;
      readonly summary: ProgressSummary;
    }
  | {
      readonly view: typeof ViewState.LOADING;
      readonly summary: ProgressSummary;
      readonly mode: ExamMode;
    }
  | {
      readonly view: typeof ViewState.EXAM;
      readonly summary: ProgressSummary;
      readonly exam: ExamRenderModel;
    }
  | {
      readonly view: typeof ViewState.RESULTS;
      readonly summary: ProgressSummary;
      readonly results: ResultsRenderModel;
    }
  | {
      readonly view: typeof ViewState.ERROR;
      readonly summary: ProgressSummary;
      readonly error: AppError;
    };

export function renderApp(root: HTMLElement, model: RenderModel): void {
  root.innerHTML = `
    <div class="app-shell">
      ${renderHeader(model.view)}
      <main id="main-content" tabindex="-1">
        ${renderView(model)}
      </main>
      ${renderFooter()}
    </div>
  `;
}

function renderView(model: RenderModel): string {
  switch (model.view) {
    case ViewState.DASHBOARD:
      return renderDashboard(model.summary);
    case ViewState.STUDY:
      return renderStudyGuide();
    case ViewState.LABS:
      return renderLabs();
    case ViewState.LOADING:
      return renderLoading(model.mode);
    case ViewState.EXAM:
      return renderExam(model.exam);
    case ViewState.RESULTS:
      return renderResults(model.results);
    case ViewState.ERROR:
      return renderError(model.error);
  }
}

function renderHeader(view: RenderModel["view"]): string {
  const dashboardCurrent = view === ViewState.DASHBOARD ? "page" : "false";
  const studyCurrent = view === ViewState.STUDY ? "page" : "false";
  const labsCurrent = view === ViewState.LABS ? "page" : "false";

  return `
    <header class="site-header">
      <button class="brand" type="button" data-action="NAV_DASHBOARD" aria-label="Edge Ready dashboard">
        <span class="brand-mark" aria-hidden="true"><span></span><span></span><span></span></span>
        <span><strong>Edge Ready</strong><small>Cloudflare certification prep</small></span>
      </button>
      <nav aria-label="Primary navigation">
        <button type="button" data-action="NAV_DASHBOARD" aria-current="${dashboardCurrent}">Dashboard</button>
        <button type="button" data-action="NAV_STUDY" aria-current="${studyCurrent}">Study guide</button>
        <button type="button" data-action="NAV_LABS" aria-current="${labsCurrent}">Practical labs</button>
      </nav>
    </header>
  `;
}

function renderDashboard(summary: ProgressSummary): string {
  const hasProgress = summary.attemptCount > 0;
  const overallScore = summary.overallPercentage ?? 0;
  const weakDomains = [...summary.domains]
    .sort((left, right) => left.percentage - right.percentage)
    .slice(0, 3);

  return `
    <section class="hero content-width">
      <div class="hero-copy">
        <p class="eyebrow">BEGINNER-FIRST · TWO-TRACK PREPARATION</p>
        <h1>Build the mental model.<br><span>Then prove it.</span></h1>
        <p class="hero-lede">Learn Application Security and Zero Trust from first principles, practice in a controlled Cloudflare environment, and use evidence—not confidence—to measure readiness.</p>
        <div class="hero-actions">
          <button class="button primary" type="button" data-action="START_EXAM" data-mode="${ExamMode.COMPREHENSIVE}">${hasProgress ? "Run a fresh diagnostic" : "Start the comprehensive diagnostic"}</button>
          <button class="button secondary" type="button" data-action="NAV_STUDY">Start with the study guide</button>
        </div>
        <p class="exam-disclaimer">Unofficial preparation aligned to current public Cloudflare documentation. It does not reproduce protected exam material or claim Cloudflare's unpublished passing score.</p>
      </div>
      <div class="readiness-card" aria-label="Readiness summary">
        <div class="score-ring" style="--score: ${overallScore}" aria-label="Overall study score ${overallScore} percent">
          <div><strong>${hasProgress ? `${overallScore}%` : "—"}</strong><span>${hasProgress ? masteryLabel(summary.overallMastery) : "Not assessed"}</span></div>
        </div>
        <div class="readiness-meta">
          <span><strong>${summary.attemptCount}</strong> completed ${summary.attemptCount === 1 ? "attempt" : "attempts"}</span>
          <span><strong>${summary.latestPercentage === null ? "—" : `${summary.latestPercentage}%`}</strong> latest score</span>
        </div>
      </div>
    </section>

    <section class="assessment-section content-width" aria-labelledby="assessment-heading">
      <div class="section-heading">
        <div><p class="eyebrow">ASSESSMENTS</p><h2 id="assessment-heading">Choose the next evidence point</h2></div>
        <p>Questions are original, scenario-based, and linked to official sources after submission.</p>
      </div>
      <div class="assessment-grid">
        ${assessmentCard(ExamMode.COMPREHENSIVE, "Both tracks", "40 balanced questions across all 16 domains.", "Recommended first")}
        ${assessmentCard(ExamMode.APPLICATION_SECURITY, "Application Security", "24 questions covering edge, WAF, rate, API, bots, and investigation.", "Track diagnostic")}
        ${assessmentCard(ExamMode.ZERO_TRUST, "Zero Trust", "24 questions covering identity, clients, connectivity, Access, Gateway, and data.", "Track diagnostic")}
        ${assessmentCard(ExamMode.FOCUS, "Weak-area practice", "12 questions loaded only from your three lowest assessed domains.", hasProgress ? "Personalized" : "Unlock after an attempt", hasProgress ? "AVAILABLE" : "LOCKED")}
      </div>
    </section>

    <section class="progress-section content-width" aria-labelledby="progress-heading">
      <div class="section-heading">
        <div><p class="eyebrow">DOMAIN MASTERY</p><h2 id="progress-heading">What to work on next</h2></div>
        <p>${hasProgress ? "Mastery is derived from every completed attempt." : "Complete a diagnostic to create your domain map."}</p>
      </div>
      ${hasProgress ? renderDomainProgress(summary.domains) : renderEmptyProgress()}
      ${hasProgress ? renderPriorityPanel(weakDomains) : ""}
    </section>
  `;
}

function assessmentCard(
  mode: ExamMode,
  title: string,
  description: string,
  label: string,
  availability: "AVAILABLE" | "LOCKED" = "AVAILABLE",
): string {
  const disabled = availability === "LOCKED" ? "disabled" : "";
  return `
    <article class="assessment-card ${availability === "LOCKED" ? "locked" : ""}">
      <div><span class="pill">${label}</span><span class="arrow" aria-hidden="true">↗</span></div>
      <h3>${title}</h3>
      <p>${description}</p>
      <button type="button" data-action="START_EXAM" data-mode="${mode}" ${disabled}>${availability === "LOCKED" ? "Complete a diagnostic first" : "Begin assessment"}</button>
    </article>
  `;
}

function renderDomainProgress(domains: readonly DomainProgress[]): string {
  const application = domains.filter((domain) =>
    APPLICATION_DOMAINS.includes(
      domain.domain as (typeof APPLICATION_DOMAINS)[number],
    ),
  );
  const zeroTrust = domains.filter((domain) => !application.includes(domain));

  return `
    <div class="mastery-columns">
      ${masteryColumn("Application Security", application)}
      ${masteryColumn("Zero Trust", zeroTrust)}
    </div>
  `;
}

function masteryColumn(title: string, domains: readonly DomainProgress[]): string {
  return `
    <div class="mastery-column">
      <h3>${title}</h3>
      ${domains.length === 0 ? '<p class="muted">Not assessed yet.</p>' : domains.map((domain) => `
        <div class="domain-row">
          <div><span>${DOMAIN_LABELS[domain.domain]}</span><strong>${domain.percentage}%</strong></div>
          <div class="progress-track"><span style="width: ${domain.percentage}%"></span></div>
          <small>${masteryLabel(domain.mastery)} · ${domain.correct}/${domain.total} correct</small>
        </div>
      `).join("")}
    </div>
  `;
}

function renderPriorityPanel(domains: readonly DomainProgress[]): string {
  return `
    <aside class="priority-panel">
      <p class="eyebrow">CURRENT PRIORITY</p>
      <h3>Return to the source, then retest</h3>
      <ol>${domains.map((domain) => `<li><span>${DOMAIN_LABELS[domain.domain]}</span><strong>${domain.percentage}%</strong></li>`).join("")}</ol>
      <button class="button primary" type="button" data-action="START_EXAM" data-mode="${ExamMode.FOCUS}">Practice these weak areas</button>
    </aside>
  `;
}

function renderEmptyProgress(): string {
  return `
    <div class="empty-state">
      <span aria-hidden="true">◎</span>
      <h3>No diagnostic evidence yet</h3>
      <p>Your first assessment creates a domain-by-domain map and unlocks focused practice.</p>
    </div>
  `;
}

function renderStudyGuide(): string {
  return `
    <section class="page-hero content-width compact">
      <p class="eyebrow">BEGINNER BOOK + QUICK REFERENCE</p>
      <h1>Learn the system in dependency order</h1>
      <p>Start with the complete 27-lesson book. Use this page as the shorter reference while you study and retest.</p>
      <div class="hero-actions"><a class="button primary" href="${beginnerBookUrl}" target="_blank" rel="noopener noreferrer">Open the complete beginner book</a></div>
    </section>
    <section class="study-layout content-width">
      <aside class="study-index" aria-label="Study guide contents">
        <strong>Contents</strong>
        ${STUDY_SECTIONS.map((section, index) => `<a href="#${section.id}"><span>${String(index + 1).padStart(2, "0")}</span>${section.title}</a>`).join("")}
      </aside>
      <div class="study-content">
        ${STUDY_SECTIONS.map((section) => `
          <article class="study-section" id="${section.id}">
            <p class="eyebrow">${section.eyebrow}</p>
            <h2>${section.title}</h2>
            <p class="study-summary">${section.summary}</p>
            <h3>What must become automatic</h3>
            <ul>${section.concepts.map((concept) => `<li>${concept}</li>`).join("")}</ul>
            <div class="checkpoint"><span>Checkpoint</span><p>${section.checkpoint}</p></div>
          </article>
        `).join("")}
      </div>
    </section>
    <section class="resources-section content-width" aria-labelledby="resources-heading">
      <div class="section-heading"><div><p class="eyebrow">OFFICIAL SOURCES</p><h2 id="resources-heading">Read and watch in this order</h2></div><p>Current Cloudflare documentation is the source of truth.</p></div>
      <div class="resource-list">
        ${[...STUDY_RESOURCES].sort((left, right) => left.sequence - right.sequence).map((resource) => `
          <a href="${resource.url}" target="_blank" rel="noopener noreferrer">
            <span class="resource-number">${String(resource.sequence).padStart(2, "0")}</span>
            <span><strong>${resource.title}</strong><small>${resource.description}</small></span>
            <span class="resource-kind">${resource.kind.replaceAll("_", " ")}</span>
          </a>
        `).join("")}
      </div>
    </section>
  `;
}

function renderLabs(): string {
  return `
    <section class="page-hero content-width compact">
      <p class="eyebrow">CONTROLLED HANDS-ON PRACTICE</p>
      <h1>Turn concepts into operating skill</h1>
      <p>Use only test hostnames, identities, devices, and services. Capture evidence before and after every configuration change.</p>
    </section>
    <section class="labs-list content-width">
      ${PRACTICAL_LABS.map((lab, index) => `
        <article class="lab-card">
          <div class="lab-number">${String(index + 1).padStart(2, "0")}</div>
          <div class="lab-body">
            <div class="lab-heading"><div><span>${lab.risk} RISK</span><h2>${lab.title}</h2></div><p>${lab.outcome}</p></div>
            <details>
              <summary>Open lab procedure</summary>
              <div class="lab-grid">
                <div><h3>Prerequisites</h3><ul>${lab.prerequisites.map((item) => `<li>${item}</li>`).join("")}</ul></div>
                <div><h3>Procedure</h3><ol>${lab.steps.map((item) => `<li>${item}</li>`).join("")}</ol></div>
                <div><h3>Evidence to retain</h3><ul>${lab.evidence.map((item) => `<li>${item}</li>`).join("")}</ul></div>
                <div><h3>Cleanup</h3><ul>${lab.cleanup.map((item) => `<li>${item}</li>`).join("")}</ul></div>
              </div>
            </details>
          </div>
        </article>
      `).join("")}
    </section>
  `;
}

function renderLoading(mode: ExamMode): string {
  return `
    <section class="loading-state content-width" aria-live="polite">
      <div class="loader" aria-hidden="true"><span></span><span></span><span></span></div>
      <p class="eyebrow">LOADING SELECTED CHUNKS</p>
      <h1>${MODE_LABELS[mode]}</h1>
      <p>Preparing a balanced question set from the relevant domains.</p>
    </section>
  `;
}

function renderExam(exam: ExamRenderModel): string {
  const question = exam.questions[exam.cursorIndex];
  if (question === undefined) {
    throw new AppError(
      "The active assessment does not contain its current question.",
      ErrorClassification.INVARIANT_VIOLATION,
      "MISSING_CURRENT_QUESTION",
    );
  }

  const answeredCount = Object.keys(exam.responses).length;
  const selectedChoiceId = exam.responses[question.id];
  const unansweredCount = exam.questions.length - answeredCount;
  const displayChoices = getDisplayChoices(question);

  return `
    <section class="exam-shell content-width">
      <div class="exam-topbar">
        <div><p class="eyebrow">${MODE_LABELS[exam.mode]}</p><strong>${answeredCount}/${exam.questions.length} answered</strong></div>
        <button type="button" class="text-button" data-action="NAV_DASHBOARD">Exit assessment</button>
      </div>
      <div class="exam-progress" aria-label="Question ${exam.cursorIndex + 1} of ${exam.questions.length}"><span style="width: ${((exam.cursorIndex + 1) / exam.questions.length) * 100}%"></span></div>
      ${exam.submissionState === SubmissionState.UNANSWERED_WARNING ? `
        <div class="submission-warning" role="alert"><strong>${unansweredCount} unanswered ${unansweredCount === 1 ? "question" : "questions"}</strong><span>Unanswered questions score as incorrect.</span><button type="button" data-action="CONFIRM_SUBMIT">Submit anyway</button></div>
      ` : ""}
      <div class="exam-layout">
        <aside class="question-map" aria-label="Question navigator">
          ${exam.questions.map((item, index) => `<button type="button" data-action="GO_TO_QUESTION" data-index="${index}" class="${index === exam.cursorIndex ? "current" : ""} ${exam.responses[item.id] !== undefined ? "answered" : ""}" aria-label="Question ${index + 1}${exam.responses[item.id] !== undefined ? ", answered" : ", unanswered"}">${index + 1}</button>`).join("")}
        </aside>
        <article class="question-card">
          <div class="question-meta"><span>Question ${exam.cursorIndex + 1} of ${exam.questions.length}</span><span>${DOMAIN_LABELS[question.domain]}</span><span>${question.difficulty.toLowerCase()}</span></div>
          <h1>${question.stem}</h1>
          <fieldset>
            <legend class="sr-only">Choose one answer</legend>
            ${displayChoices.map(({ choice, displayId }) => `
              <label class="choice ${selectedChoiceId === choice.id ? "selected" : ""}">
                <input type="radio" name="answer" value="${choice.id}" data-question-id="${question.id}" ${selectedChoiceId === choice.id ? "checked" : ""} />
                <span class="choice-id">${displayId}</span><span>${choice.label}</span>
              </label>
            `).join("")}
          </fieldset>
          <div class="question-actions">
            <button class="button secondary" type="button" data-action="PREVIOUS_QUESTION" ${exam.cursorIndex === 0 ? "disabled" : ""}>Previous</button>
            ${exam.cursorIndex < exam.questions.length - 1
              ? '<button class="button primary" type="button" data-action="NEXT_QUESTION">Next question</button>'
              : '<button class="button primary" type="button" data-action="SUBMIT_EXAM">Submit assessment</button>'}
          </div>
        </article>
      </div>
    </section>
  `;
}

function renderResults(results: ResultsRenderModel): string {
  const resultByQuestion = new Map(
    results.report.results.map((result) => [result.questionId, result]),
  );
  const weakest = [...results.report.domainScores]
    .sort((left, right) => left.percentage - right.percentage)
    .slice(0, 3);

  return `
    <section class="results-hero content-width">
      <div>
        <p class="eyebrow">ASSESSMENT COMPLETE</p>
        <h1>${results.report.percentage}%</h1>
        <h2>${masteryLabel(results.report.mastery)}</h2>
        <p>${results.report.correct} correct out of ${results.report.total}. This score is a study diagnostic, not Cloudflare's unpublished exam score.</p>
        <div class="hero-actions"><button class="button primary" type="button" data-action="START_EXAM" data-mode="${ExamMode.FOCUS}">Practice weakest areas</button><button class="button secondary" type="button" data-action="NAV_DASHBOARD">Return to dashboard</button></div>
      </div>
      <div class="result-priorities">
        <span>Focus next</span>
        ${weakest.map((domain) => `<div><strong>${DOMAIN_LABELS[domain.domain]}</strong><span>${domain.percentage}%</span></div>`).join("")}
      </div>
    </section>
    <section class="results-domains content-width">
      <div class="section-heading"><div><p class="eyebrow">SCORE MAP</p><h2>Domain results</h2></div></div>
      ${renderDomainProgress(results.report.domainScores)}
    </section>
    <section class="review-section content-width">
      <div class="section-heading"><div><p class="eyebrow">ANSWER REVIEW</p><h2>Reason from the source</h2></div><p>Every explanation links to current official Cloudflare documentation.</p></div>
      <div class="review-list">
        ${results.questions.map((question, index) => {
          const result = resultByQuestion.get(question.id);
          if (result === undefined) {
            throw new AppError(
              "A completed assessment is missing a question result.",
              ErrorClassification.INVARIANT_VIOLATION,
              "MISSING_QUESTION_RESULT",
            );
          }
          const selected = question.choices.find((choice) => choice.id === result.selectedChoiceId);
          const correct = question.choices.find((choice) => choice.id === question.correctChoiceId);
          if (correct === undefined) {
            throw new AppError(
              "A question is missing its declared correct choice.",
              ErrorClassification.INVARIANT_VIOLATION,
              "MISSING_CORRECT_CHOICE",
            );
          }
          const stateClass = result.outcome.toLowerCase();
          return `
            <details class="review-item ${stateClass}" ${result.outcome !== AnswerOutcome.CORRECT ? "open" : ""}>
              <summary><span>${String(index + 1).padStart(2, "0")}</span><strong>${question.stem}</strong><em>${result.outcome}</em></summary>
              <div class="review-body">
                <p><span>Your answer</span>${selected?.label ?? "Unanswered"}</p>
                <p><span>Correct answer</span>${correct.label}</p>
                <div class="explanation"><strong>Why</strong><p>${question.explanation}</p></div>
                <a href="${question.source.url}" target="_blank" rel="noopener noreferrer">Read the official source: ${question.source.title} ↗</a>
              </div>
            </details>
          `;
        }).join("")}
      </div>
    </section>
  `;
}

function renderError(error: AppError): string {
  return `
    <section class="error-state content-width" role="alert">
      <span>${error.classification}</span>
      <h1>The study app stopped explicitly.</h1>
      <p>${error.message}</p>
      <code>${error.code}</code>
      <button class="button primary" type="button" data-action="NAV_DASHBOARD">Return to dashboard</button>
    </section>
  `;
}

function renderFooter(): string {
  return `
    <footer>
      <div><strong>Edge Ready</strong><span>Unofficial Cloudflare certification preparation</span></div>
      <p>Cloudflare and related marks belong to Cloudflare, Inc. This independent study tool uses public documentation and original questions.</p>
    </footer>
  `;
}

function masteryLabel(mastery: MasteryState): string {
  switch (mastery) {
    case MasteryState.NOT_ASSESSED:
      return "Not assessed";
    case MasteryState.FOUNDATIONAL:
      return "Foundational";
    case MasteryState.DEVELOPING:
      return "Developing";
    case MasteryState.PROFICIENT:
      return "Proficient";
    case MasteryState.READY:
      return "Ready standard";
  }
}
